/**
 * DeleteSchoolDialog — 2-step confirmation dialog for permanently deleting a school.
 *
 * Step 1: Preview counts of what will be deleted (users, sessions, evidence, etc.).
 * Step 2: The Super Admin MUST type the school's exact name to enable the red
 *         "Eliminar permanentemente" button. Extra safety layer.
 *
 * Backend cascade covers: users_v2, user_sessions, student_profiles,
 * teacher_student_assignments, chat_sessions, support_bot_sessions,
 * quota_alerts, internal_messages, magic_links, learning_evidence, schools.
 * `audit_logs` are preserved.
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Trash2, AlertTriangle, Loader2 } from "lucide-react";

const COUNT_LABELS = {
  users_total: "Usuarios totales",
  students: "Estudiantes",
  teachers: "Docentes",
  institutional_admins: "Admins institucionales",
  student_profiles: "Perfiles de estudiante",
  assignments: "Asignaciones docente-alumno",
  chat_sessions: "Sesiones del Tutor IA",
  support_bot_sessions: "Sesiones del Bot de Soporte",
  quota_alerts: "Alertas de cupo",
  internal_messages: "Mensajes internos",
  audit_logs: "Registros de auditoría (se conservan)",
};

export default function DeleteSchoolDialog({ school, onDeleted }) {
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmName, setConfirmName] = useState("");
  const [deleting, setDeleting] = useState(false);

  const loadPreview = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/admin/schools-v2/${school.id}/delete-preview`,
        { withCredentials: true },
      );
      setPreview(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo cargar la vista previa");
      setOpen(false);
    } finally {
      setLoading(false);
    }
  }, [school.id]);

  useEffect(() => {
    if (open) {
      setConfirmName("");
      setPreview(null);
      loadPreview();
    }
  }, [open, loadPreview]);

  const doDelete = async () => {
    if (confirmName.trim() !== (school.name || "").trim()) return;
    setDeleting(true);
    try {
      const res = await axios.delete(
        `${API}/admin/schools-v2/${school.id}`,
        { params: { confirm_name: confirmName.trim() }, withCredentials: true },
      );
      toast.success(`"${school.name}" fue movida a la papelera (7 días)`);
      setOpen(false);
      if (onDeleted) onDeleted(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo eliminar la escuela");
    } finally {
      setDeleting(false);
    }
  };

  const nameMatches = confirmName.trim() === (school.name || "").trim();
  const counts = preview?.counts || {};
  const totalImpact =
    (counts.users_total || 0) +
    (counts.student_profiles || 0) +
    (counts.assignments || 0) +
    (counts.chat_sessions || 0) +
    (counts.support_bot_sessions || 0) +
    (counts.internal_messages || 0);

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="text-red-600 hover:text-red-700 hover:bg-red-50 gap-1"
          data-testid={`delete-school-${school.id}`}
        >
          <Trash2 className="w-3.5 h-3.5" />
          Eliminar
        </Button>
      </AlertDialogTrigger>

      <AlertDialogContent
        className="max-w-lg"
        data-testid="delete-school-dialog"
        onEscapeKeyDown={(e) => deleting && e.preventDefault()}
      >
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2 text-red-700">
            <AlertTriangle className="w-5 h-5" />
            Mover escuela a la papelera
          </AlertDialogTitle>
          <AlertDialogDescription className="text-gray-600">
            La escuela <b className="text-gray-900">{school.name}</b> se moverá a la <b>papelera durante 7 días</b>.
            Durante ese periodo podrás <b>restaurarla</b> desde la pestaña &ldquo;Papelera&rdquo;.
            Después de 7 días se eliminará <b>permanentemente</b> junto con todos sus datos.
          </AlertDialogDescription>
        </AlertDialogHeader>

        {/* Preview */}
        {loading && (
          <div className="flex items-center justify-center py-6 text-gray-400 text-sm">
            <Loader2 className="w-4 h-4 animate-spin mr-2" /> Cargando vista previa…
          </div>
        )}

        {preview && !loading && (
          <div className="bg-amber-50 border border-amber-100 rounded-xl p-4 max-h-[280px] overflow-y-auto">
            <p className="text-xs font-semibold text-amber-800 uppercase tracking-wide mb-2">
              Se congelarán ({totalImpact} registros vinculados)
            </p>
            <p className="text-[11px] text-gray-500 mb-2">
              Los usuarios de esta escuela no podrán iniciar sesión durante los 7 días
              en papelera. Al restaurar, todo vuelve a estar disponible.
            </p>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
              {Object.entries(counts).map(([k, v]) => (
                <div key={k} className="flex items-center justify-between">
                  <span className="text-gray-600">{COUNT_LABELS[k] || k}</span>
                  <span className={`font-bold ${v > 0 ? "text-amber-700" : "text-gray-400"}`}>
                    {v}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Name confirmation */}
        {preview && !loading && (
          <div className="space-y-2 pt-2">
            <label className="text-sm font-medium text-gray-700">
              Escribe <span className="font-mono text-red-700">{school.name}</span> para confirmar:
            </label>
            <Input
              value={confirmName}
              onChange={(e) => setConfirmName(e.target.value)}
              placeholder={school.name}
              disabled={deleting}
              autoComplete="off"
              data-testid="delete-school-confirm-input"
              className={nameMatches ? "border-red-400 focus:border-red-500" : ""}
            />
          </div>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel disabled={deleting} data-testid="delete-school-cancel">
            Cancelar
          </AlertDialogCancel>
          <AlertDialogAction
            asChild
            disabled={!nameMatches || deleting || loading}
          >
            <Button
              onClick={(e) => { e.preventDefault(); doDelete(); }}
              disabled={!nameMatches || deleting || loading}
              className="bg-amber-600 hover:bg-amber-700 text-white gap-1.5"
              data-testid="delete-school-confirm-btn"
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
              Mover a la papelera
            </Button>
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
